/**
 * Access Log Service - Epic 38.2
 * Service para consulta de logs de acesso (login/logout/refresh)
 */

import apiClient from './apiClient';

export interface AccessLog {
  id: number;
  usuario: string | null;
  usuario_id: number | null;
  email_tentativa: string | null;
  empresa: string | null;
  acao: 'LOGIN' | 'LOGOUT' | 'REFRESH_TOKEN';
  sucesso: boolean;
  motivo_falha: string | null;
  ip: string | null;
  user_agent?: string | null;
  device_info: {
    browser?: string;
    os?: string;
    device_type?: string;
  } | null;
  location: {
    city?: string;
    country?: string;
  } | null;
  data_hora: string;
}

export interface AccessLogFilters {
  page?: number;
  page_size?: number;
  acao?: string[];
  sucesso?: boolean[];
  usuario?: string;
  ip?: string;
  data_inicio?: string;
  data_fim?: string;
  sort?: string;
}

export interface PaginatedAccessLogs {
  results: AccessLog[];
  count: number;
  total_pages: number;
  page: number;
  page_size: number;
}

export interface ActionOption {
  value: string;
  label: string;
  count: number;
}

/**
 * Listar logs de acesso com filtros e paginação
 */
export const getAccessLogs = async (filters: AccessLogFilters = {}): Promise<PaginatedAccessLogs> => {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.page_size) params.append('page_size', filters.page_size.toString());
  if (filters.acao && filters.acao.length > 0) {
    filters.acao.forEach(a => params.append('acao', a));
  }
  if (filters.sucesso && filters.sucesso.length > 0) {
    filters.sucesso.forEach(s => params.append('sucesso', s.toString()));
  }
  if (filters.usuario) params.append('usuario', filters.usuario);
  if (filters.ip) params.append('ip', filters.ip);
  if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
  if (filters.data_fim) params.append('data_fim', filters.data_fim);
  if (filters.sort) params.append('sort', filters.sort);

  const response = await apiClient.get(`/logs/access/?${params.toString()}`);
  return response.data;
};

/**
 * Obter detalhes de um log específico
 */
export const getAccessLogDetail = async (logId: number): Promise<AccessLog> => {
  const response = await apiClient.get(`/logs/access/${logId}`);
  return response.data;
};

/**
 * Listar ações disponíveis com contagem
 */
export const getActionsList = async (): Promise<ActionOption[]> => {
  const response = await apiClient.get('/logs/access/actions/list');
  return response.data;
};

/**
 * Exportar logs para XLSX
 */
export const exportAccessLogsXlsx = async (filters: AccessLogFilters = {}): Promise<Blob> => {
  const params = new URLSearchParams();

  if (filters.acao && filters.acao.length > 0) {
    filters.acao.forEach(a => params.append('acao', a));
  }
  if (filters.sucesso && filters.sucesso.length > 0) {
    filters.sucesso.forEach(s => params.append('sucesso', s.toString()));
  }
  if (filters.usuario) params.append('usuario', filters.usuario);
  if (filters.ip) params.append('ip', filters.ip);
  if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
  if (filters.data_fim) params.append('data_fim', filters.data_fim);
  if (filters.sort) params.append('sort', filters.sort);

  const response = await apiClient.get(`/logs/access/export/xlsx?${params.toString()}`, {
    responseType: 'blob'
  });
  return response.data;
};

export default {
  getAccessLogs,
  getAccessLogDetail,
  getActionsList,
  exportAccessLogsXlsx
};
